import React, { createContext, useContext } from 'react';

// True while the analytics page is being captured into the PDF report.
// Components read it to show what they normally only show on hover
// (exact numbers, tooltip values, full titles) and to drop buttons/animations.
// Value: false (normal page) or the PDF variant being generated.
export const PdfModeContext = createContext<false | 'desktop' | 'mobile'>(false);
export const usePdfMode = () => !!useContext(PdfModeContext);
export const usePdfVariant = () => useContext(PdfModeContext);

// Compact table printed under a chart in PDF mode, styled like the page tables.
export function PdfDataTable({
  columns,
  rows,
  swatches,
}: {
  columns: { label: string; align?: 'left' | 'right' }[];
  rows: React.ReactNode[][];
  swatches?: (string | undefined)[];
}) {
  if (!rows?.length) return null;
  return (
    <table className="w-full mt-3" style={{ fontSize: 13, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#f8fafc' }}>
          {columns.map((c, i) => (
            <th
              key={i}
              style={{
                textAlign: c.align || (i === 0 ? 'left' : 'right'),
                padding: '6px 8px',
                fontWeight: 500,
                color: '#475569',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, r) => (
          <tr key={r} style={{ borderBottom: '1px solid #f1f5f9' }}>
            {row.map((cell, i) => (
              <td
                key={i}
                style={{ textAlign: columns[i]?.align || (i === 0 ? 'left' : 'right'), padding: '5px 8px', color: '#0f172a' }}
              >
                {i === 0 && swatches?.[r] ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span
                      style={{ width: 10, height: 10, borderRadius: 2, background: swatches[r], display: 'inline-block', marginRight: 8 }}
                    />
                    {cell}
                  </span>
                ) : (
                  cell
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Shorten long captions for the PDF (the page shows them in a hover tooltip)
export const pdfText = (t: any, n = 120) => {
  const s = String(t ?? '').replace(/\s+/g, ' ').trim();
  return s.length > n ? `${s.slice(0, n).trim()}…` : s;
};

// Values of a time-series chart (what its tooltip shows), for charts with too
// many points to label each one on the line itself.
export function PdfSeriesGrid({ data, format }: { data: any[]; format?: (v: any) => string }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const fmt = format || ((v: any) => (v == null ? '-' : Number(v).toLocaleString('en-IN')));
  return (
    <div
      className="mt-3"
      style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '2px 16px', fontSize: 12, color: '#334155' }}
    >
      {data.map((d: any, i: number) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #e2e8f0', padding: '2px 0' }}>
          <span style={{ color: '#64748b' }}>{d?.date ?? '-'}</span>
          <span>{fmt(d?.value)}</span>
        </div>
      ))}
    </div>
  );
}
